import React, { useState } from "react";
import ZModal from "@atoms/ZModal";
import { ZButton, ZIcon, ZTypography } from "@atoms";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import ZViewUserRequestModalUserDetailRow from "./ZViewUserRequestModalUserDetailRow";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { ZMessage, ZSpin } from "@molecules";
import {
	PendingUsersModalMode,
	UserRole,
	UserAccess,
	PricingPlanEstimateData,
	PricingPlanEstimateRequest,
	PricingPlanTierEnum,
	PricingPlanNames,
	BillingStatusEnum,
	PendingUser
} from "@/Contracts/Users";
import { IViewUserRequestModalProps } from "./interface";
import ZViewUserRequestModalPlanSummary from "./ZViewUserRequestModalPlanSummary";
import { Form } from "antd";
import {
	EWidgets,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import { ZForm } from "@organisms";
import "./index.less";
import { useComponentDidUpdate } from "@/Hooks";
import { getPendingUserDetails, planEstimate } from "@/Services/User";
import {
	ChildEntityHydrationParams,
	includeChildEntities
} from "@/Contracts";

export const ZViewUserRequestModal: React.FC<IViewUserRequestModalProps> = ({
	visible,
	onClose,
	billingInfo,
	currentPlan,
	subscriptionDetails,
	size,
	pendingUserId,
	addUser,
	addVendor,
	rejectUser,
	currentUserCount,
	disableTransition,
	showGoBackButton = true
}) => {
	const [pricingPlanData, setPricingPlanData] =
		useState<PricingPlanEstimateData | null>(null);
	const [pricingPlanEstimatesLoading, setPricingPlanEstimatesLoading] =
		useState<boolean>(false);
	const [userDetailsLoading, setUserDetailsLoading] =
		useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [addUserLoading, setAddUserLoading] = useState<boolean>(false);
	const [form] = Form.useForm();
	const userRoleOptions = [
		{
			value: UserAccess.CompanyLevel,
			name: "Global"
		},
		{
			value: UserAccess.SelfLevel,
			name: "Assigned"
		}
	];
	const userPermissionOptions = [
		{
			value: UserRole.PrimaryRole,
			name: "Admin"
		},
		{
			value: UserRole.SecondaryRole,
			name: "Manager"
		},
		{
			value: UserRole.UserRole,
			name: "User"
		},
		{
			value: UserRole.ApiUserRole,
			name: "API User"
		}
	];
	const [pendingUserModalMode, setPendingUserModalMode] =
		useState<PendingUsersModalMode>();
	const [pendingUser, setPendingUser] = useState<PendingUser | null>(null);
	const fetchPendingUser = async () => {
		try {
			setUserDetailsLoading(true);
			const includedChildEntities = includeChildEntities([
				ChildEntityHydrationParams.USERS_ALL,
				ChildEntityHydrationParams.VENDORS_ALL
			]);

			const user = await getPendingUserDetails(
				pendingUserId,
				includedChildEntities
			);
			setPendingUser(user);
			setPendingUserModalMode(
				user.signup_initiating_vendor && !user.is_vendor_accepted
					? PendingUsersModalMode.REQUEST
					: PendingUsersModalMode.REJECT
			);
		} catch (error) {
			console.error(error);
		} finally {
			setUserDetailsLoading(false);
		}
	};
	const handleAddUser = async () => {
		if (!pendingUserId) return;
		const level = form.getFieldValue("level");
		const role = form.getFieldValue("role");
		try {
			setAddUserLoading(true);
			await addUser(
				pendingUserId,
				pendingUser?.user.name.full ?? "",
				pendingUser?.user.email ?? "",
				level,
				role
			);
			form.resetFields();
			setAddUserLoading(false);
		} catch (error) {
			console.error(error);
		}
	};
	const handleRejectUser = async () => {
		if (!pendingUserId) return;
		try {
			await rejectUser(pendingUserId);
		} catch (error) {
			console.error(error);
		}
	};
	const acceptUserRequest = () => {
		setPendingUserModalMode(PendingUsersModalMode.ADD);
	};
	const acceptOrRejectUserAndJoinVendor = async (
		mode: PendingUsersModalMode
	) => {
		if (!pendingUserId) return;
		try {
			setLoading(true);
			const pendingUserResponse = await addVendor(pendingUserId);
			setPendingUser(pendingUserResponse);
			ZMessage.success("Vendor joined successfully");
			setPendingUserModalMode(mode);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};
	const rejectUserRequest = () => {
		ZModal.confirm({
			title: (
				<ZTypography.ZText
					colorVariant={9}
					weight={EFontWeights.MEDIUM}
					level={2}
				>
					Are you sure you want to reject user request to join your
					team account?
				</ZTypography.ZText>
			),
			onOk: handleRejectUser,
			okText: "Yes, Reject",
			okType: "primary",
			icon: (
				<ZIcon
					name={EIcons.CIRCLEQUESTION}
					color={EColors.GOLD}
					colorVariant={6}
					className="anticon"
					size="md"
				/>
			),
			centered: true
		});
	};
	const getPricingPlanEstimates = async () => {
		try {
			setPricingPlanEstimatesLoading(true);
			const pricingPlanEstimateRequest = new PricingPlanEstimateRequest();
			pricingPlanEstimateRequest.no_of_seats =
				subscriptionDetails.total_quantity + 1;
			pricingPlanEstimateRequest.plan = currentPlan?.name
				? currentPlan.name
				: PricingPlanNames.LAUNCH;
			pricingPlanEstimateRequest.period = currentPlan?.plan_tiers
				? currentPlan?.plan_tiers.find(tier => tier.active)?.period ??
				  PricingPlanTierEnum.MONTHLY
				: PricingPlanTierEnum.MONTHLY;
			pricingPlanEstimateRequest.addons = currentPlan?.add_ons.map(
				addOn => addOn.id
			);
			const planEstimates = await planEstimate(
				pricingPlanEstimateRequest
			);
			setPricingPlanData(planEstimates);
		} catch (error) {
			console.error(error);
		} finally {
			setPricingPlanEstimatesLoading(false);
		}
	};
	useComponentDidUpdate(() => {
		if (visible) {
			fetchPendingUser();
			if (
				billingInfo.billing_status === BillingStatusEnum.Active &&
				currentPlan
			) {
				getPricingPlanEstimates();
			}
		}
	}, [visible]);
	const sections: IFormSectionProps[] = [
		{
			shouldHideSectionTopPadding: true,
			hideDivider: true,
			meta: {
				formItemLayout: [6, 10],
				fields: [
					{
						label: "User Name",
						key: "username",
						widget: EWidgets.INPUT,
						disabled: true,
						initialValue: pendingUser?.user.name.full,
						widgetProps: {
							width: "full"
						}
					},
					{
						wrapperCol: 24,
						key: "title",
						render: () => (
							<ZTypography.ZText
								level={1}
								color={EColors.GRAY}
								colorVariant={9}
								weight={EFontWeights.MEDIUM}
								className="mb-4 block"
							>
								Set permission and role for the new user
							</ZTypography.ZText>
						)
					},
					{
						label: "Permission level",
						key: "level",
						widget: EWidgets.SELECT,
						required: true,
						message: "Please set permission level",
						widgetProps: {
							placeholder: "Set permission level",
							width: "full",
							selectOptions: userPermissionOptions
						}
					},
					{
						label: "User Role",
						key: "role",
						widget: EWidgets.SELECT,
						required: true,
						message: "Please set user role",
						widgetProps: {
							placeholder: "Set user Role",
							width: "full",
							selectOptions: userRoleOptions
						}
					}
				]
			}
		}
	];
	const formProps: IFormProps = {
		key: "addUserForm",
		sections,
		onFinish: handleAddUser,
		form: form,
		isSubmitAllowed: false
	};
	const rejectAndAcceptVendorHandler =
		pendingUserModalMode === PendingUsersModalMode.REQUEST
			? () =>
					acceptOrRejectUserAndJoinVendor(
						PendingUsersModalMode.REJECT
					)
			: pendingUserModalMode === PendingUsersModalMode.ADD
			? onClose
			: rejectUserRequest;

	const addAndAcceptVendorHandler =
		pendingUserModalMode === PendingUsersModalMode.REQUEST
			? () => acceptOrRejectUserAndJoinVendor(PendingUsersModalMode.ADD)
			: pendingUserModalMode === PendingUsersModalMode.ADD
			? () => form.submit()
			: acceptUserRequest;

	return (
		<ZModal
			title={
				pendingUserModalMode === PendingUsersModalMode.REQUEST
					? `Request to join partner group`
					: pendingUserModalMode === PendingUsersModalMode.REJECT
					? `User Request`
					: `Add User`
			}
			key="viewUserRequestModal"
			size={size}
			visible={visible}
			onCancel={onClose}
			hideOkButton={true}
			hideCancelButton={true}
			modalBodyClassName="h-138"
			transitionName={disableTransition ? "" : undefined}
			mask={disableTransition ? false : true}
			footer={
				<div className="flex align-items-center justify-between px-2 py-0.5">
					<div>
						{showGoBackButton && (
							<ZButton
								type="link"
								iconName={EIcons.ARROWLEFT}
								iconSize="small"
								iconPlacement="left"
								className="p-0"
								onClick={onClose}
							>
								Go Back
							</ZButton>
						)}
					</div>
					<div>
						<ZButton
							danger={
								pendingUserModalMode ===
								PendingUsersModalMode.REJECT
							}
							ghost={
								pendingUserModalMode ===
								PendingUsersModalMode.REJECT
							}
							onClick={rejectAndAcceptVendorHandler}
						>
							{pendingUserModalMode ===
							PendingUsersModalMode.REQUEST
								? `Reject User and Accept Vendor`
								: pendingUserModalMode ===
								  PendingUsersModalMode.ADD
								? `Cancel`
								: `Reject User request`}
						</ZButton>
						<ZButton
							type="primary"
							onClick={addAndAcceptVendorHandler}
							disabled={addUserLoading}
						>
							{pendingUserModalMode ===
							PendingUsersModalMode.REQUEST
								? `Accept User and Join Vendor`
								: pendingUserModalMode ===
								  PendingUsersModalMode.ADD
								? `Add User`
								: `Accept User Request`}
						</ZButton>
					</div>
				</div>
			}
		>
			<ZSpin
				spinning={
					loading || pricingPlanEstimatesLoading || userDetailsLoading
				}
			>
				{pendingUserModalMode !== PendingUsersModalMode.ADD && (
					<ZTypography.ZText
						className="block"
						colorVariant={10}
						level={1}
						weight={EFontWeights.MEDIUM}
					>
						{pendingUserModalMode === PendingUsersModalMode.REQUEST
							? `${
									pendingUser?.user.name.full ?? ""
							  } has been invited by ${
									pendingUser?.vendor_company?.name
							  } to join them. Do you want to add this user in your partner account?`
							: `You have successfully joined the vendor. Do you want to reject the user request to join your team?`}
					</ZTypography.ZText>
				)}
				{pendingUserModalMode !== PendingUsersModalMode.ADD && (
					<ZViewUserRequestModalUserDetailRow
						username={pendingUser?.user.name.full ?? ""}
						useremail={pendingUser?.user.email ?? ""}
					/>
				)}
				{pendingUserModalMode === PendingUsersModalMode.ADD && (
					<ZForm {...formProps} />
				)}
				{pendingUserModalMode !== PendingUsersModalMode.REJECT &&
					pricingPlanData &&
					currentUserCount === subscriptionDetails.total_quantity && (
						<ZViewUserRequestModalPlanSummary
							plan={pricingPlanData}
						/>
					)}

				{pendingUserModalMode === PendingUsersModalMode.REQUEST && (
					<div className="align-text-top grid grid-flow-col justify-start mt-4">
						<ZIcon
							name={EIcons.INFOCIRCLE}
							size="sm"
							className="mt-1"
							color={EColors.GRAY}
							colorVariant={7}
						/>
						<ZTypography.ZText
							level={1}
							color={EColors.GRAY}
							colorVariant={7}
							className="ml-2"
						>
							Tip: To avoid extra billing, you can join the vendor
							without adding user to your team account.
						</ZTypography.ZText>
					</div>
				)}
			</ZSpin>
		</ZModal>
	);
};
